// Spectral analysis helpers: SNR, harmonic energy ratio, onset clarity.

import { powerSpectrum } from './fft';

export interface SpectralFeatures {
  snr: number; // dB
  her: number; // harmonic energy ratio, first 3 harmonics / total
  totalEnergy: number;
}

// SNR estimate: compare peak-region energy to noise-floor energy.
export function estimateSNR(signal: Float32Array, sampleRate: number, fundamental: number): number {
  if (fundamental <= 0) return 0;
  const spectrum = powerSpectrum(signal);
  const binHz = sampleRate / signal.length;
  const f0Bin = Math.round(fundamental / binHz);

  let signalEnergy = 0;
  // Sum energy in harmonic bins (1..5) within a tolerance window.
  for (let h = 1; h <= 5; h++) {
    const center = f0Bin * h;
    for (let b = center - 1; b <= center + 1; b++) {
      if (b >= 0 && b < spectrum.length) signalEnergy += spectrum[b];
    }
  }

  // Noise floor: median of the spectrum (robust to peaks).
  const sorted = Float32Array.from(spectrum).sort();
  const median = sorted[sorted.length >> 1];
  const noiseEnergy = median * spectrum.length;

  if (noiseEnergy <= 0 || signalEnergy <= 0) return 0;
  const snr = 10 * Math.log10(signalEnergy / noiseEnergy);
  return Math.max(0, Math.min(60, snr));
}

// Harmonic Energy Ratio = energy of first 3 harmonics / total energy (FR-3).
export function harmonicEnergyRatio(signal: Float32Array, sampleRate: number, fundamental: number): number {
  if (fundamental <= 0) return 0;
  const spectrum = powerSpectrum(signal);
  const binHz = sampleRate / signal.length;
  const f0Bin = Math.round(fundamental / binHz);

  let harmonicEnergy = 0;
  for (let h = 1; h <= 3; h++) {
    const center = f0Bin * h;
    for (let b = center - 1; b <= center + 1; b++) {
      if (b >= 0 && b < spectrum.length) harmonicEnergy += spectrum[b];
    }
  }
  let total = 0;
  for (let i = 0; i < spectrum.length; i++) total += spectrum[i];
  if (total <= 0) return 0;
  return harmonicEnergy / total;
}

export function spectralFeatures(signal: Float32Array, sampleRate: number, fundamental: number): SpectralFeatures {
  const her = harmonicEnergyRatio(signal, sampleRate, fundamental);
  const snr = estimateSNR(signal, sampleRate, fundamental);
  const spectrum = powerSpectrum(signal);
  let totalEnergy = 0;
  for (let i = 0; i < spectrum.length; i++) totalEnergy += spectrum[i];
  return { snr, her, totalEnergy };
}

// Onset clarity: ratio of attack RMS to sustained RMS, normalised to [0,1] (ideal 2..5).
export function onsetClarity(frames: Float32Array[]): number {
  if (frames.length < 4) return 0;
  const rms = (f: Float32Array) => {
    let s = 0;
    for (let i = 0; i < f.length; i++) s += f[i] * f[i];
    return Math.sqrt(s / f.length);
  };
  const attackRMS = rms(frames[0]);
  let sustained = 0;
  for (let i = 2; i < frames.length; i++) sustained += rms(frames[i]);
  sustained /= frames.length - 2;
  if (sustained <= 0) return 0;
  const ratio = attackRMS / sustained; // ideal 2..5
  // Map: 0->0, 2..5->~1, beyond 8->1 (clipped). Simple normalisation.
  if (ratio <= 0) return 0;
  if (ratio >= 5) return 1;
  return Math.min(1, ratio / 5);
}
